import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { Box, CircularProgress } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { store, persistor } from '@/store';
import { AppThemeProvider } from '@/providers/ThemeProvider';
import { ResponsiveProvider } from '@/providers/ResponsiveProvider';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RequireAuth } from '@/auth/RequireAuth';
import { RequireRole } from '@/auth/RequireRole';
import { PRODUCTION_ADMIN_ROLE, QA_ADMIN_ROLE, SUPER_ADMIN_ROLE } from '@/auth/roleChecks';
import { MainLayout } from '@/components/Layout/MainLayout';
import { validateEnvironment, Config, ENV } from '@/config/environment';
import { initSentry } from '@/helpers/sentry';
import { GlobalLoadingIndicator } from '@/components/GlobalLoadingIndicator';
import { GlobalErrorDisplay } from '@/components/GlobalErrorDisplay';
import { SessionExpiredDialog } from '@/components/SessionExpiredDialog';
import { SessionMonitor } from '@/components/SessionMonitor';
import { NetworkStatusProvider } from '@/components/ErrorRecovery';
import { preloadCriticalRoutes, addRoutePreloading } from '@/utils/routePreloading';
import { RoutePrefetcher } from '@/components/RoutePrefetcher';
import { WebVitalsOverlay } from '@/components/WebVitalsOverlay';
import { BrowserCompatibilityCheck } from '@/components/BrowserCompatibilityCheck';

// Lazy load route components
const LoginPage = lazy(() => import('@/pages/LoginPage').then(m => ({ default: m.LoginPage })));
const OAuthRedirect = lazy(() => import('@/pages/OAuthRedirect').then(m => ({ default: m.OAuthRedirect })));
const HomePage = lazy(() => import('@/pages/HomePage'));
const SftpSyncModule = lazy(() => import('@/pages/sftp-sync').then(m => ({ default: m.SftpSyncModule })));
const TestGraphQL = lazy(() => import('@/pages/TestGraphQL').then(m => ({ default: m.TestGraphQL })));
const OnHandProcessRoutes = lazy(() => import('@/pages/on-hand-process'));
const InventoryModule = lazy(() => import('@/pages/inventory'));
const CsvImportModule = lazy(() => import('@/features/csv-import').then(m => ({ default: m.CsvImportModule })));
const OrderLookupModule = lazy(() => import('@/features/order-lookup-v2').then(m => ({ default: m.OrderLookupFeature })));
const OrderLineItemsModule = lazy(() => import('@/features/order-line-items'));
const VirtualScrollingDemo = lazy(() => import('@/pages/VirtualScrollingDemo').then(m => ({ default: m.VirtualScrollingDemo })));
const ProjectionStatus = lazy(() => import('@/pages/projection-status').then(m => ({ default: m.ProjectionStatus })));
const TimeLogger = lazy(() => import('@/pages/time-logger'));
const ResetTenantPage = lazy(() => import('@/pages/reset-tenant').then(m => ({ default: m.ResetTenantPage })));
const DeleteStreamsPage = lazy(() => import('@/pages/delete-streams').then(m => ({ default: m.DeleteStreamsPage })));
const AdminTenantsPage = lazy(() => import('@/pages/admin-tenant/AdminTenantsPage'));
const AdminTenantDetailPage = lazy(() => import('@/pages/admin-tenant/AdminTenantDetailPage'));
const TrialPipelinePage = lazy(() => import('@/pages/trial-pipeline/TrialPipelinePage'));
const ContractRequestsPage = lazy(() => import('@/pages/contract-requests/ContractRequestsPage'));
const TrialPolicyPage = lazy(() => import('@/pages/trial-policy/TrialPolicyPage'));
const FeatureFlagsPage = lazy(() => import('@/pages/feature-flags/FeatureFlagsPage').then(m => ({ default: m.FeatureFlagsPage })));
const EmailDeliveryPage = lazy(() => import('@/pages/email-delivery/EmailDeliveryPage'));
const FernieConversationsPage = lazy(
  () => import('@/pages/fernie-conversations/FernieConversationsPage')
);
const WhatsNewPage = lazy(() => import('@/pages/whats-new/WhatsNewPage'));
const DataCorrectionsPage = lazy(() => import('@/pages/data-corrections/DataCorrectionsPage'));
const TestLoginsPage = lazy(() => import('@/pages/test-logins/TestLoginsPage'));
const PaymentProcessorsPage = lazy(() => import('@/pages/payment-processors/PaymentProcessorsPage'));
const ParentCompaniesPage = lazy(() => import('@/pages/parent-companies/ParentCompaniesPage'));
const DeviceFlashPage = lazy(() => import('@/pages/devices/DeviceFlashPage'));
const DeviceProvisionPage = lazy(() => import('@/pages/devices/DeviceProvisionPage'));
const FirmwareFilesPage = lazy(() => import('@/pages/firmware/FirmwareFilesPage'));
const AcrPage = lazy(() => import('@/pages/acr/AcrPage'));
const PortalCustomDomainsPage = lazy(() => import('@/pages/portal-custom-domains/PortalCustomDomainsPage'));
const IotPage = lazy(() => import('@/pages/iot/IotPage'));
const DeployDashboardPage = lazy(() => import('@/pages/deploy-dashboard/DeployDashboardPage'));
const QaDashboardPage = lazy(() => import('@/pages/qa-dashboard/QaDashboardPage'));
const BackgroundServicesPage = lazy(() => import('@/pages/background-service-config/BackgroundServicesPage'));
const DailyTransactionSummariesPage = lazy(() => import('@/pages/daily-transaction-summaries').then(m => ({ default: m.DailyTransactionSummariesPage })));
const EntityCheckpointsPage = lazy(() => import('@/pages/entity-checkpoints').then(m => ({ default: m.EntityCheckpointsPage })));
const ApplicationsListPage = lazy(() => import('@/pages/role-mgmt/applications').then(m => ({ default: m.ApplicationsListPage })));
const ApplicationUsersPage = lazy(() => import('@/pages/role-mgmt/applications').then(m => ({ default: m.ApplicationUsersPage })));
const GroupsListPage = lazy(() => import('@/pages/role-mgmt/groups/GroupsListPage'));
const GroupDetailPage = lazy(() => import('@/pages/role-mgmt/groups/GroupDetailPage'));
const UsersSearchPage = lazy(() => import('@/pages/role-mgmt/users').then(m => ({ default: m.UsersSearchPage })));
const UserDetailPage = lazy(() => import('@/pages/role-mgmt/users').then(m => ({ default: m.UserDetailPage })));
const InvitesPage = lazy(() => import('@/pages/role-mgmt/invites').then(m => ({ default: m.InvitesPage })));
const RestockCalcResultsPage = lazy(() => import('@/pages/restock-calc-results/RestockCalcResultsPage'));
const RestockDataValidityPage = lazy(() => import('@/pages/restock-data-validity/RestockDataValidityPage'));
const RestockDataSeedingPage = lazy(() => import('@/pages/restock-data-seeding/RestockDataSeedingPage'));
const ScavengePage = lazy(() => import('@/pages/scavenge').then(m => ({ default: m.ScavengePage })));
const AggregatePage = lazy(() => import('@/pages/event-stream-aggregate').then(m => ({ default: m.AggregatePage })));

const Forbidden = () => <div>Forbidden</div>;

// Initialize app
const initializeApp = () => {
  try {
    validateEnvironment();
    initSentry();
    
    // Set up route preloading
    preloadCriticalRoutes();
    addRoutePreloading();
  } catch (error) {
    console.error('App initialization error:', error);
  }
};

// Loading component
const LoadingScreen = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

function App() {
  useEffect(() => {
    console.log('App component mounting...');
    try {
      initializeApp();
      console.log('App initialized successfully');
    } catch (error) {
      console.error('App initialization failed:', error);
    }
    
    // Add global error handler to suppress specific JSON parsing errors
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason instanceof Error && 
          event.reason.message.includes('Unexpected token') && 
          event.reason.message.includes('Unauthorized')) {
        // Prevent this specific error from being shown
        event.preventDefault();
      }
    };
    
    const handleError = (event: ErrorEvent) => {
      if (event.error instanceof Error && 
          event.error.message.includes('Unexpected token') && 
          event.error.message.includes('Unauthorized')) {
        // Prevent this specific error from being shown
        event.preventDefault();
      }
    };
    
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return (
    <BrowserCompatibilityCheck>
      <ErrorBoundary>
        <Provider store={store}>
          <PersistGate loading={<LoadingScreen />} persistor={persistor}>
            <AppThemeProvider>
              <ResponsiveProvider>
                <NetworkStatusProvider>
                  <GlobalLoadingIndicator />
                  <GlobalErrorDisplay />
                  <SessionExpiredDialog />
                  <SessionMonitor />
                  {/* Web Vitals overlay in development */}
                  {ENV.isDev && Config.features.webVitalsDevOverlay && <WebVitalsOverlay />}
                  <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
              />
              <BrowserRouter>
                <RoutePrefetcher />
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<Suspense fallback={<LoadingScreen />}><LoginPage /></Suspense>} />
                  <Route path="/oauth-redirect" element={<Suspense fallback={<LoadingScreen />}><OAuthRedirect /></Suspense>} />
                  <Route path="/forbidden" element={<Forbidden />} />
                  
                  {/* Protected routes with layout */}
                  <Route
                    path="/"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <HomePage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />
                  
                  {/* SFTP Sync Module */}
                  <Route
                    path="/sftp-sync/*"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <SftpSyncModule />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />
                  
                  {/* On Hand Process Module */}
                  <Route
                    path="/on-hand-process/*"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <OnHandProcessRoutes />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />
                  
                  {/* Inventory Module */}
                  <Route
                    path="/inventory"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <InventoryModule />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />
                  
                  {/* Projection Status */}
                  <Route
                    path="/projection-status"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <ProjectionStatus />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  {/* Entity Checkpoints */}
                  <Route
                    path="/entity-checkpoints"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <EntityCheckpointsPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  {/* Scavenge */}
                  <Route
                    path="/scavenge"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <ScavengePage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  {/* Event Stream Aggregate */}
                  <Route
                    path="/event-stream-aggregate"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <AggregatePage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />
                  
                  {/* CSV Import Module */}
                  <Route
                    path="/csv-import/*"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <CsvImportModule />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />
                  
                  {/* Order Lookup Module */}
                  <Route
                    path="/order-lookup-v2/*"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <OrderLookupModule />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />
                  
                  {/* Order Line Items Module */}
                  <Route
                    path="/order-line-items"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <OrderLineItemsModule />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />
                  
                  {/* Time Logger Module */}
                  <Route
                    path="/time-logger"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <TimeLogger />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />
                  
                  {/* Reset Tenant */}
                  <Route
                    path="/reset-tenant"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <ResetTenantPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  {/* Role Management — Applications */}
                  <Route
                    path="/role-mgmt/applications"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <ApplicationsListPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  {/* Role Management — Application Users */}
                  <Route
                    path="/role-mgmt/applications/:appId"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <ApplicationUsersPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="/role-mgmt/groups"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <GroupsListPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="/role-mgmt/groups/:groupId"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <GroupDetailPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  {/* Role Management — Users */}
                  <Route
                    path="/role-mgmt/users"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <UsersSearchPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  {/* Role Management — User Detail */}
                  <Route
                    path="/role-mgmt/users/:userId"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <UserDetailPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  {/* Role Management — Invites */}
                  <Route
                    path="/role-mgmt/invites"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <InvitesPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  {/* Delete Streams */}
                  <Route
                    path="/delete-streams"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <DeleteStreamsPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  {/* Admin Tenants */}
                  <Route
                    path="/admin-tenants"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <AdminTenantsPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/admin-tenants/:tenantId"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <AdminTenantDetailPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/trial-pipeline"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <TrialPipelinePage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/contract-requests"
                    element={
                      <RequireAuth>
                        <RequireRole role={SUPER_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <ContractRequestsPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/trial-policy"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <TrialPolicyPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  {/* Background Services */}
                  <Route
                    path="/background-services"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <BackgroundServicesPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="/restock-admin/daily-transaction-summaries"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <DailyTransactionSummariesPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  {/* Restock Calc Results */}
                  <Route
                    path="/restock-admin/dbr-calc-results"
                    element={<Navigate to="/restock-admin/restock-calc-results" replace />}
                  />
                  <Route
                    path="/restock-admin/restock-calc-results"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <RestockCalcResultsPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  {/* Restock Data Validity */}
                  <Route
                    path="/restock-admin/data-validity"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <RestockDataValidityPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  {/* Restock Data Seeding - non-prod only (server also gates via adminRestockSeedingEnabled) */}
                  {!ENV.isProd && (
                    <Route
                      path="/restock-admin/data-seeding"
                      element={
                        <RequireAuth>
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <RestockDataSeedingPage />
                            </Suspense>
                          </MainLayout>
                        </RequireAuth>
                      }
                    />
                  )}

                  {/* Feature Flags */}
                  <Route
                    path="/feature-flags"
                    element={
                      <RequireAuth>
                        <RequireRole role={SUPER_ADMIN_ROLE} behavior="forbid">
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <FeatureFlagsPage />
                          </Suspense>
                        </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />

                  {/* Email Delivery */}
                  <Route
                    path="/email-delivery"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <EmailDeliveryPage />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />

                  {/* Fernie Conversations: read-only staff view, gated by superadmin role */}
                  <Route
                    path="/fernie-conversations"
                    element={
                      <RequireAuth>
                        <RequireRole role={SUPER_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <FernieConversationsPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />

                  {/* What's new admin: tenant preview, feature and release edits, gated by superadmin role */}
                  <Route
                    path="/whats-new"
                    element={
                      <RequireAuth>
                        <RequireRole role={SUPER_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <WhatsNewPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />

                  <Route path="/devices/register" element={<Navigate to="/devices/provision" replace />} />

                  {/* EdgeBox firmware flashing over Web Serial — same superadmin/factory gate */}
                  <Route
                    path="/devices/flash"
                    element={
                      <RequireAuth>
                        <RequireRole role={SUPER_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <DeviceFlashPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="/devices/provision"
                    element={
                      <RequireAuth>
                        <RequireRole role={SUPER_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <DeviceProvisionPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />

                  {/* Published firmware container — same superadmin gate as the surface it calls */}
                  <Route
                    path="/firmware-files"
                    element={
                      <RequireAuth>
                        <RequireRole role={SUPER_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <FirmwareFilesPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />

                  {/* Data Corrections — gated by superadmin role */}
                  <Route
                    path="/data-corrections"
                    element={
                      <RequireAuth>
                        <RequireRole role={SUPER_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <DataCorrectionsPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/data-corrections/:runId"
                    element={
                      <RequireAuth>
                        <RequireRole role={SUPER_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <DataCorrectionsPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />

                  {/* Test Logins — gated by superadmin role */}
                  <Route
                    path="/test-logins"
                    element={
                      <RequireAuth>
                        <RequireRole role={SUPER_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <TestLoginsPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />

                  {/* Payment Processors (T28) — gated by superadmin role */}
                  <Route
                    path="/payment-processors"
                    element={
                      <RequireAuth>
                        <RequireRole role={SUPER_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <PaymentProcessorsPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />

                  {/* Parent Companies — gated by superadmin role */}
                  <Route
                    path="/parent-companies"
                    element={
                      <RequireAuth>
                        <RequireRole role={SUPER_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <ParentCompaniesPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />

                  {/* ACR Image Tag Management (Deploy Dashboard) — gated by productionAdmin role */}
                  <Route
                    path="/acr"
                    element={
                      <RequireAuth>
                        <RequireRole role={PRODUCTION_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <AcrPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />

                  {/* Portal Custom Domains — gated by productionAdmin role */}
                  <Route
                    path="/portal-custom-domains"
                    element={
                      <RequireAuth>
                        <RequireRole role={PRODUCTION_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <PortalCustomDomainsPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />

                  {/* IoT Fleet — gated by productionAdmin role */}
                  <Route
                    path="/iot"
                    element={
                      <RequireAuth>
                        <RequireRole role={PRODUCTION_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <IotPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />

                  {/* QA Dashboard — gated by qaAdmin role (superadmin bypasses) */}
                  <Route
                    path="/qa-dashboard"
                    element={
                      <RequireAuth>
                        <RequireRole role={QA_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <QaDashboardPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />

                  {/* Local-only preview of the QA Dashboard: renders the page without
                      auth so it can be reviewed with `npm run dev` before the wsapi
                      feed and qaAdmin role exist. Registered ONLY when
                      VITE_ENVIRONMENT is development/test — never in stage or prod. */}
                  {ENV.isDev && (
                    <Route
                      path="/qa-dashboard-preview"
                      element={
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <QaDashboardPage />
                          </Suspense>
                        </MainLayout>
                      }
                    />
                  )}

                  {/* Deploy Dashboard — gated by productionAdmin role */}
                  <Route
                    path="/deploy-dashboard"
                    element={
                      <RequireAuth>
                        <RequireRole role={PRODUCTION_ADMIN_ROLE} behavior="forbid">
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <DeployDashboardPage />
                            </Suspense>
                          </MainLayout>
                        </RequireRole>
                      </RequireAuth>
                    }
                  />

                  {/* Settings Module - Hidden until infrastructure is ready */}
                  {/* <Route
                    path="/settings/*"
                    element={
                      <RequireAuth>
                        <SettingsModule />
                      </RequireAuth>
                    }
                  /> */}
                  
                  {/* Test GraphQL */}
                  <Route
                    path="/test-graphql"
                    element={
                      <RequireAuth>
                        <MainLayout>
                          <Suspense fallback={<LoadingScreen />}>
                            <TestGraphQL />
                          </Suspense>
                        </MainLayout>
                      </RequireAuth>
                    }
                  />
                  
                  {/* Virtual Scrolling Demo - dev only */}
                  {import.meta.env.DEV && (
                    <Route
                      path="/virtual-scrolling-demo"
                      element={
                        <RequireAuth>
                          <MainLayout>
                            <Suspense fallback={<LoadingScreen />}>
                              <VirtualScrollingDemo />
                            </Suspense>
                          </MainLayout>
                        </RequireAuth>
                      }
                    />
                  )}
                  
                  
                  {/* Default redirect */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </BrowserRouter>
              </NetworkStatusProvider>
            </ResponsiveProvider>
          </AppThemeProvider>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
    </BrowserCompatibilityCheck>
  );
}

export default App;
